import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SectionProps {
  /** Anchor id, so the section can be linked to directly. */
  id?: string;
  /** Two-digit index printed in mono before the heading. */
  n?: string;
  kicker?: string;
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  className?: string;
  /** `wide` uses the full site width; `narrow` caps at 48rem for prose. */
  width?: 'wide' | 'narrow';
  tone?: 'paper' | 'white' | 'ink';
}

/** A page section: numbered heading, optional lead, hairline rule above. */
export function Section({ id, n, kicker, title, lead, children, className, width = 'wide', tone = 'paper' }: SectionProps) {
  return (
    <section
      id={id}
      className={cn(
        'scroll-mt-16 border-t border-line',
        tone === 'white' && 'bg-paper-2',
        tone === 'ink' && 'bg-surface border-t-accent/60',
        className
      )}
    >
      <div className={cn('mx-auto px-4 py-12 sm:px-6 md:py-20', width === 'wide' ? 'max-w-site' : 'max-w-3xl')}>
        <div className="mb-8 md:mb-12">
          <div className="mb-3 flex items-center gap-3">
            {n && <span className={cn('font-mono text-xs tabular', 'text-accent-ink')}>{n}</span>}
            {kicker && <span className="label">{kicker}</span>}
          </div>
          <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">{title}</h2>
          {lead && (
            <div className={cn('mt-4 max-w-3xl text-base leading-relaxed sm:text-lg', 'text-ink-2')}>
              {lead}
            </div>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}
